import { fmtDate, fmtQty, isExpired } from '../format';
import { STOCK_TYPE_META } from '../status';
import type {
  FieldError,
  GoodsIssueLine,
  GoodsIssueSource,
  UpdateGoodsIssueLinesPayload,
  WarehouseStockType,
} from '../types';

/**
 * Pure model of the picking "Edit picked items" table: rows (SKU · location ·
 * batch · qty), the labels of what a row has chosen, live validation (per-SKU
 * totals vs the order, duplicate location + batch, qty vs available, missing
 * fields), the PUT payload and the mapping of a 422 back onto rows. No React
 * here — EditablePickingTable renders it.
 */

/** PUT /goods-issues/:id/lines takes at most this many rows. */
export const MAX_EDIT_ROWS = 200;

/** One ordered SKU (the "Ordered item" table). */
export interface OrderedSku {
  sku: string;
  itemName: string;
  uomName: string;
  qty: number;
}

/** Where a row pointed when edit mode opened (shown until candidates load). */
export interface RowSeed {
  storageID: string;
  storageName: string;
  storagePath: string;
  stockID: string;
  batchNumber: string;
  expiredDate: string | null;
}

export interface EditRow {
  /** Stable React key (rows can be removed from the middle). */
  key: number;
  sku: string;
  storageID: string;
  /** Location + batch = one stock row. */
  stockID: string;
  /** Raw input ("12", "1,5"). */
  qty: string;
  seed: RowSeed | null;
}

export type RowField = 'sku' | 'storageID' | 'stockID' | 'qty';
/** A field of a row, or the row as a whole (`row`). */
export type RowErrorKey = RowField | 'row';
export type RowErrors = Partial<Record<RowErrorKey, string>>;

let seq = 0;

/** An empty row ("+ Tambah baris"). */
export const newEditRow = (): EditRow => ({
  key: ++seq,
  sku: '',
  storageID: '',
  stockID: '',
  qty: '',
  seed: null,
});

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Rows from the goods issue's current lines: one row per stock row (lines
 * that share a stock row are merged — the server collapses them too), qty =
 * the reserved qty.
 */
export const rowsFromLines = (lines: GoodsIssueLine[]): EditRow[] => {
  const byStock = new Map<string, EditRow>();
  const out: EditRow[] = [];
  lines.forEach((l) => {
    const cur = byStock.get(l.stockID);
    if (cur) {
      cur.qty = String(round2(Number(cur.qty) + l.qtyReserved));
      return;
    }
    const row: EditRow = {
      key: ++seq,
      sku: l.sku,
      storageID: l.storageID,
      stockID: l.stockID,
      qty: String(l.qtyReserved),
      seed: {
        storageID: l.storageID,
        storageName: l.storageName,
        storagePath: l.storagePath,
        stockID: l.stockID,
        batchNumber: l.batchNumber,
        expiredDate: l.expiredDate,
      },
    };
    byStock.set(l.stockID, row);
    out.push(row);
  });
  return out;
};

/* ------------------------------- row edits ------------------------------- */

/** Another SKU: location, batch and qty no longer apply. */
export const withSku = (row: EditRow, sku: string): EditRow =>
  sku === row.sku ? row : { ...row, sku, storageID: '', stockID: '', qty: '' };

/** Another location: the batch belonged to the old one. */
export const withStorage = (row: EditRow, storageID: string): EditRow =>
  storageID === row.storageID ? row : { ...row, storageID, stockID: '' };

export const withStock = (row: EditRow, stockID: string): EditRow =>
  stockID === row.stockID ? row : { ...row, stockID };

export const withQty = (row: EditRow, qty: string): EditRow =>
  qty === row.qty ? row : { ...row, qty };

/**
 * The ordered SKUs of a goods issue from its current lines (reserved qty =
 * ordered qty), in first-seen order — what the picking page feeds the editor.
 */
export const orderedFromLines = (lines: GoodsIssueLine[]): OrderedSku[] => {
  const by = new Map<string, OrderedSku>();
  lines.forEach((l) => {
    const cur = by.get(l.sku);
    if (cur) cur.qty = round2(cur.qty + l.qtyReserved);
    else
      by.set(l.sku, {
        sku: l.sku,
        itemName: l.itemName,
        uomName: l.uomName,
        qty: l.qtyReserved,
      });
  });
  return Array.from(by.values());
};

/**
 * A new row for `sku`, placed right after that SKU's last row (the end when it
 * has none), qty = what is still missing when that is > 0.
 */
export const addRowFor = (
  rows: readonly EditRow[],
  sku: string,
  remaining: number
): EditRow[] => {
  const row: EditRow = {
    ...newEditRow(),
    sku,
    qty: remaining > 0 ? String(round2(remaining)) : '',
  };
  let at = -1;
  rows.forEach((r, i) => {
    if (r.sku === sku) at = i;
  });
  const next = [...rows];
  next.splice(at < 0 ? next.length : at + 1, 0, row);
  return next;
};

/* ------------------------------ source rows ------------------------------ */

/**
 * What the table knows about the stock rows a goods issue can reserve from.
 * Location and batch are server-searched pickers, so it never holds "every
 * candidate": only the rows a user picked and the rows of the locations the
 * current lines point at (`lists`, complete for one SKU at one storage).
 */
export interface SourceKnowledge {
  /** Candidate rows known so far, by stock row id. */
  candidates: ReadonlyMap<string, GoodsIssueSource>;
  /** SKU + storage ({@link sourceKey}) → every stock row id it holds. */
  lists: ReadonlyMap<string, ReadonlySet<string>>;
}

export const NO_SOURCES: SourceKnowledge = {
  candidates: new Map(),
  lists: new Map(),
};

/** One SKU held in one storage. */
export const sourceKey = (sku: string, storageID: string): string =>
  `${sku}\u0001${storageID}`;

/** The candidate a row points at (null until chosen / loaded / found). */
export const candidateOf = (
  known: SourceKnowledge,
  row: EditRow
): GoodsIssueSource | null =>
  (row.stockID && known.candidates.get(row.stockID)) || null;

/** The row's stock row is known to be gone: its location's list lacks it. */
export const stockGone = (known: SourceKnowledge, row: EditRow): boolean => {
  const list = known.lists.get(sourceKey(row.sku, row.storageID));
  return !!row.stockID && !!list && !list.has(row.stockID);
};

/* -------------------------------- labels --------------------------------- */

/** "Rak Utama / Section A / Slot A1" — the name path, the bare name without one. */
export const storageLabel = (name: string, path: string): string =>
  path || name;

/**
 * "20270101-100726-aB3x · Return · Exp 01 Dec 2026 · 12 tersedia" / "… · Tanpa
 * expiry" (the stock type and the available qty once known).
 */
export const batchLabel = (
  batchNumber: string,
  expiredDate: string | null,
  available?: number,
  stockType?: WarehouseStockType | null
): string =>
  [
    batchNumber,
    ...(stockType ? [STOCK_TYPE_META[stockType].label] : []),
    expiredDate
      ? `Exp ${fmtDate(expiredDate)}${isExpired(expiredDate) ? ' · expired' : ''}`
      : 'Tanpa expiry',
    ...(available === undefined ? [] : [`${fmtQty(available)} tersedia`]),
  ].join(' · ');

/** "2027-01-05" → "05/01/2027" (the raw text when it is not a day). */
const dmy = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};

/**
 * One stock row as a single picker option (location + batch together):
 * "Rak / Section / Slot · batch 2027… · exp 05/01/2027 · available 12".
 */
export const candidateLabel = (
  c: Pick<
    GoodsIssueSource,
    'storageName' | 'storagePath' | 'batchNumber' | 'expiredDate' | 'stockType'
  >,
  available?: number
): string =>
  [
    storageLabel(c.storageName, c.storagePath),
    `batch ${c.batchNumber}`,
    ...(c.stockType ? [STOCK_TYPE_META[c.stockType].label.toLowerCase()] : []),
    c.expiredDate ? `exp ${dmy(c.expiredDate)}` : 'tanpa exp',
    ...(available === undefined ? [] : [`available ${fmtQty(available)}`]),
  ].join(' · ');

export const UNAVAILABLE = ' (tidak tersedia)';

/**
 * What the Item location picker shows for the row's chosen location when the
 * list doesn't hold it: the label it was picked with, else the one the line
 * started from; flagged once its location is known to hold nothing of the SKU.
 */
export const locationLabelOf = (
  row: EditRow,
  picked: ReadonlyMap<string, string>,
  known: SourceKnowledge
): string | undefined => {
  if (!row.storageID) return undefined;
  const seed = row.seed;
  const base =
    picked.get(row.storageID) ??
    (seed && seed.storageID === row.storageID
      ? storageLabel(seed.storageName, seed.storagePath)
      : row.storageID);
  const list = known.lists.get(sourceKey(row.sku, row.storageID));
  return list && list.size === 0 ? base + UNAVAILABLE : base;
};

/** The Batch picker's label for the row's chosen stock row. */
export const batchLabelOf = (
  row: EditRow,
  known: SourceKnowledge
): string | undefined => {
  if (!row.stockID) return undefined;
  const gone = stockGone(known, row);
  const c = known.candidates.get(row.stockID);
  const seed = row.seed;
  const base = c
    ? batchLabel(
        c.batchNumber,
        c.expiredDate,
        gone ? undefined : c.available,
        c.stockType
      )
    : seed && seed.stockID === row.stockID
      ? batchLabel(seed.batchNumber, seed.expiredDate)
      : row.stockID;
  return gone ? base + UNAVAILABLE : base;
};

/* ------------------------------- validation ------------------------------ */

/** "12", "1,5", "0.25" → number when it has at most 2 decimals; else null. */
export const parseEditQty = (s: string): number | null => {
  const t = s.trim().replace(',', '.');
  return /^\d+(\.\d{1,2})?$/.test(t) ? Number(t) : null;
};

export interface RowCheck {
  errors: RowErrors;
  /** Fields still empty. */
  missing: RowField[];
}

export interface SkuTotal {
  sku: string;
  ordered: number;
  total: number;
  /** total − ordered: < 0 short, > 0 over. */
  diff: number;
}

export interface EditCheck {
  rows: RowCheck[];
  totals: SkuTotal[];
  /** Row indexes with an empty field. */
  incomplete: number[];
  /** Row indexes with an error. */
  invalid: number[];
  totalsOk: boolean;
  canSave: boolean;
  /** Some SKU is still short (and the row cap isn't reached). */
  canAdd: boolean;
}

/** Same wording as the server's 422s where they overlap. */
export const MSG = {
  qtyPositive: 'Qty harus lebih dari 0.',
  qtyDecimals: 'Qty maksimal 2 angka desimal.',
  notOrdered: 'SKU ini tidak ada di order',
  gone: 'Lokasi & batch ini sudah tidak tersedia',
  duplicate: (row: number) =>
    `Lokasi & batch ini sudah dipakai di baris ${row}`,
  overAvailable: (n: number) => `Maksimal ${fmtQty(n)} dari lokasi & batch ini`,
} as const;

export const checkRows = (
  rows: readonly EditRow[],
  ordered: readonly OrderedSku[],
  known: SourceKnowledge
): EditCheck => {
  const orderedSkus = new Set(ordered.map((o) => o.sku));
  const firstByStock = new Map<string, number>();
  const sums = new Map<string, number>();

  const checks = rows.map((row, i): RowCheck => {
    const errors: RowErrors = {};
    const missing: RowField[] = [];
    if (!row.sku) missing.push('sku');
    if (!row.storageID) missing.push('storageID');
    if (!row.stockID) missing.push('stockID');
    if (!row.qty.trim()) missing.push('qty');

    if (row.sku && !orderedSkus.has(row.sku)) errors.sku = MSG.notOrdered;

    const raw = row.qty.trim().replace(',', '.');
    const qty = raw ? parseEditQty(raw) : null;
    if (raw && qty === null) {
      errors.qty = /^\d*\.\d{3,}$/.test(raw)
        ? MSG.qtyDecimals
        : MSG.qtyPositive;
    } else if (qty !== null && qty <= 0) {
      errors.qty = MSG.qtyPositive;
    }
    if (row.sku && qty !== null && qty > 0) {
      sums.set(row.sku, round2((sums.get(row.sku) ?? 0) + qty));
    }

    if (row.stockID) {
      const first = firstByStock.get(row.stockID);
      if (first !== undefined) {
        errors.stockID = MSG.duplicate(first + 1);
      } else {
        firstByStock.set(row.stockID, i);
        // Availability waits for the row's candidate to be known.
        const cand = candidateOf(known, row);
        if (
          stockGone(known, row) ||
          (cand && cand.storageID !== row.storageID)
        ) {
          errors.stockID = MSG.gone;
        } else if (cand && qty !== null && qty > 0 && qty > cand.available) {
          errors.qty = MSG.overAvailable(cand.available);
        }
      }
    }
    return { errors, missing };
  });

  const totals = ordered.map((o) => {
    const total = sums.get(o.sku) ?? 0;
    return { sku: o.sku, ordered: o.qty, total, diff: round2(total - o.qty) };
  });
  const incomplete = checks.flatMap((c, i) => (c.missing.length ? [i] : []));
  const invalid = checks.flatMap((c, i) =>
    Object.keys(c.errors).length ? [i] : []
  );
  const totalsOk = totals.every((t) => t.diff === 0);
  return {
    rows: checks,
    totals,
    incomplete,
    invalid,
    totalsOk,
    canSave:
      rows.length > 0 && totalsOk && !incomplete.length && !invalid.length,
    canAdd: rows.length < MAX_EDIT_ROWS && totals.some((t) => t.diff < 0),
  };
};

/** Normalised content (order-free): did the user change anything? */
const signature = (rows: readonly EditRow[]): string =>
  rows
    .map((r) => {
      const q = parseEditQty(r.qty);
      return `${r.sku}\u0001${r.stockID}\u0001${q === null ? r.qty.trim() : q}`;
    })
    .sort()
    .join('\u0002');

export const isDirty = (
  initial: readonly EditRow[],
  rows: readonly EditRow[]
): boolean => signature(initial) !== signature(rows);

/** The PUT body — row i is `lines[i]` (422s point back at it). */
export const toPayload = (
  rows: readonly EditRow[]
): UpdateGoodsIssueLinesPayload => ({
  lines: rows.map((r) => ({
    sku: r.sku,
    stockID: r.stockID,
    qty: parseEditQty(r.qty) ?? Number(r.qty),
  })),
});

/* ---------------------------- server errors ------------------------------ */

const LINE_FIELD = /^lines(?:\[(\d+)\]|\.(\d+))(?:\.(\w+))?$/;
const FIELD_KEY: Record<string, RowErrorKey> = {
  sku: 'sku',
  storageID: 'storageID',
  stockID: 'stockID',
  qty: 'qty',
};

/**
 * A 422's field errors onto the rows that were sent (`lines[i].qty` → row
 * i's qty), keyed by row key so they stay on their row; anything that names
 * no row (e.g. `lines`) is a form-level message.
 */
export const mapServerErrors = (
  errors: readonly FieldError[],
  rows: readonly EditRow[]
): { byKey: Map<number, RowErrors>; form: string[] } => {
  const byKey = new Map<number, RowErrors>();
  const form: string[] = [];
  errors.forEach((e) => {
    const m = LINE_FIELD.exec(e.field);
    const raw = m ? (m[1] ?? m[2]) : null;
    const index =
      raw !== null && raw !== undefined
        ? Number(raw)
        : typeof e.lineIndex === 'number'
          ? e.lineIndex
          : null;
    const row = index !== null ? rows[index] : undefined;
    if (!row) {
      form.push(e.message);
      return;
    }
    const key: RowErrorKey = (m?.[3] && FIELD_KEY[m[3]]) || 'row';
    const cur = byKey.get(row.key) ?? {};
    if (!cur[key]) cur[key] = e.message;
    byKey.set(row.key, cur);
  });
  return { byKey, form };
};
